/**
 * Шесть состояний, ради которых этот каркас и существует.
 *
 * В измерительном продукте они не «крайние случаи», а обычная жизнь экрана:
 * замер идёт ночами, движки падают поодиночке, квота кончается в середине
 * месяца, а половина ролей вообще ничего не меняет. Правило одно и оно из
 * app-kit.md §5: **неполнота раскрывается на том же экране, а не в журнале.**
 * Молча выродившееся число — худшее, что может сделать интерфейс замера.
 *
 * Что именно закрыто:
 *
 * 1. `NeverMeasured`  — замера ещё не было;
 * 2. `Measuring`      — замер идёт (шаги без счётчика, полоса со счётчиком);
 * 3. `PartialRun`     — замер прошёл не целиком;
 * 4. `NoDataInPeriod` — за выбранный период точек нет;
 * 5. `QuotaExhausted` — тариф исчерпан или замеры остановлены;
 * 6. `ReadOnlyNotice` — роль только смотрит.
 *
 * Спиннера здесь нет ни одного: у каждой операции есть либо счётчик, либо шаг.
 *
 * Строки живут в каталоге (`state.*`, B5). Счётные — целиком на языке ICU:
 * формы выбирает `Intl.PluralRules` активной локали, а не тройка слов на месте
 * вызова.
 */

import type { ReactNode } from 'react';

import { Empty, Pipeline, Progress } from './state';
import { t } from '../i18n';
import { dateProse, period } from '@shared/src/format';
import type { components } from '../api/schema';

type Run = components['schemas']['Run'];
type RunDetail = components['schemas']['RunDetail'];
type OrganizationQuotas = components['schemas']['OrganizationQuotas'];

/**
 * Запасная причина остановки замеров.
 *
 * Показывается только когда сервер не прислал `scan_blocked_reason`: его
 * формулировка точнее, она знает дату и тариф. Фраза лежала литералом в
 * четырёх экранах — «Тариф», «Обзор бренда», «Семантическое ядро» и заведение
 * бренда, — и первая же правка развела бы их молча. Хвост у каждого экрана свой
 * и правильно свой: там говорится, что на этом экране всё-таки можно делать.
 */
export function scanBlockedFallback(): string {
  return t('state.scan-blocked-fallback');
}

/**
 * Бренд не выбран.
 *
 * Шесть экранов писали это шестью разными способами и с четырьмя разными
 * подзаголовками. Действие у всех одно, и написано оно теперь один раз; экранам
 * остаётся сказать, что именно они покажут, когда бренд выберут.
 */
export function pickBrand(): string {
  return t('state.pick-brand');
}

/**
 * Шаги конвейера замера. Первая фаза — разбор сайта, сбор вопросов и фильтрация
 * — счётчика не даёт вовсе (спека §6, шаги 1–3), поэтому показываются шаги, а
 * не неопределённый индикатор.
 */
export function scanSteps(): string[] {
  return [
    t('state.step.site'),
    t('state.step.questions'),
    t('state.step.engines'),
    t('state.step.count'),
  ];
}

/* ─────────────────────────── 1. Замера ещё не было ────────────────────────── */

/**
 * Пусто. Не «нет данных», а «мы ещё не спрашивали»: разница для клиента
 * принципиальная, потому что во втором случае виноват не он.
 */
export function NeverMeasured({
  brandName,
  action,
}: {
  brandName?: string;
  action?: ReactNode;
}) {
  return (
    <Empty
      eyebrow={t('state.never.eyebrow')}
      title={
        brandName ? t('state.never.title-named', { brandName }) : t('state.never.title')
      }
      action={action}
    >
      {t('state.never.body')}
    </Empty>
  );
}

/* ───────────────────────────── 2. Замер идёт ──────────────────────────────── */

/**
 * Идущий замер.
 *
 * Пока сетка не размечена (`planned_tasks = 0`), считать нечего — показываются
 * шаги. Как только план известен, встаёт определённая полоса и моношрифтовая
 * строка настоящих событий: сколько задач сделано и сколько движков закрыто.
 *
 * Числа берутся из прогона, а не из таймера: полоса, которая едет сама по себе,
 * в продукте про измерения — обман.
 */
export function Measuring({
  run,
  compact = false,
}: {
  run: Run | RunDetail;
  compact?: boolean;
}) {
  const planned = run.planned_tasks ?? 0;
  const done = run.done_tasks ?? 0;
  const failed = run.failed_tasks ?? 0;
  const engines = 'engines' in run ? (run.engines ?? []) : [];
  const enginesDone = engines.filter(
    (e) => e.tasks_done + e.tasks_failed >= e.tasks_total,
  ).length;

  // Сетка ещё не размечена или очередь не тронута — считать нечего.
  if (run.state === 'queued' || planned === 0) {
    return (
      <div className="running" role="status">
        <p className="stamp">{t('state.measuring.queued')}</p>
        <Pipeline steps={scanSteps()} current={0} />
      </div>
    );
  }

  const parts = [t('state.measuring.done', { done, planned })];
  if (engines.length > 0) {
    // «0 из 5 движков» без глагола читается как «ни один движок не работает» —
    // ровно наоборот тому, что происходит. Считается закрытая часть сетки, и
    // это надо назвать, а не оставить числу догадываться о своём смысле.
    parts.push(
      t('state.measuring.engines', { done: enginesDone, total: engines.length }),
    );
  }
  if (failed > 0) parts.push(t('state.measuring.failures', { failed }));

  return (
    <div className="running">
      <Progress done={done} total={planned} caption={parts.join(' · ')} />
      {!compact ? <Pipeline steps={scanSteps()} current={2} /> : null}

      {'cost_capped' in run && run.cost_capped ? (
        <p className="stamp warn">{t('state.measuring.cost-capped')}</p>
      ) : null}
    </div>
  );
}

/* ─────────────────────── 3. Замер прошёл не целиком ───────────────────────── */

/**
 * Замер завершился в состоянии `partial` либо часть движков отвалилась.
 *
 * Показывается **вместе с числами**, а не вместо них: цифры за неполную сетку
 * остаются на экране, но рядом стоит, чего в них не хватает. Молчаливое
 * округление до «всё хорошо» — то самое, чего измерительный интерфейс делать не
 * должен.
 *
 * Предохранитель движка — техническое, а не падение бренда, и это говорится
 * прямо (app-kit.md §4).
 *
 * Строка про закрытые задачи проверена на 0, 1, 2, 5, 11, 21, 101, 111 — падеж
 * после «из N» и хвост про ошибки живут в самой ICU-строке, конструкция «ещё
 * N — с ошибкой» согласования не требует ни при каком значении (см.
 * `tools/check-copy.mjs`).
 */
export function PartialRun({
  run,
  action,
  engineTitle,
}: {
  run: RunDetail | Run;
  action?: ReactNode;
  /**
   * Название движка по его коду. Клиенту показывается `Яндекс Нейро`, а не
   * `yandex_neuro`: код — это ключ каталога, и в тексте про сорвавшийся замер
   * он выглядит как ошибка продукта. Каталог приходит из `GET /engines`, где у
   * движка есть `title`; экран, у которого каталога под рукой нет, ничего не
   * передаёт и печатает код — это честнее, чем зашитая таблица движков, которая
   * разъедется с каталогом молча.
   */
  engineTitle?: (code: string) => string;
}) {
  const name = engineTitle ?? ((code: string) => code);
  const engines = 'engines' in run ? (run.engines ?? []) : [];
  const broken = engines.filter((e) => e.circuit_broken);
  const failed = run.failed_tasks ?? 0;
  const planned = run.planned_tasks ?? 0;
  const done = run.done_tasks ?? 0;

  return (
    <div className="notice warn" role="status">
      {/* Заголовок говорит про ЗАМЕР, а не про сетку. «Сетка» — наше слово из
          спеки: клиент встречает его в этой полосе раньше, чем где-либо ещё, и
          первое, что он видит при сбое, оказывается термином, которого он не
          знает. Расшифровка сетки стоит ниже, под числами. */}
      <b>{t('state.partial.title')}</b>
      {planned > 0
        ? t('state.partial.closed', { done, planned, failed })
        : t('state.partial.fallback')}

      {broken.length > 0 ? (
        <span className="stamp">
          {t('state.partial.broken', {
            engines: broken.map((e) => name(e.engine_code)).join(', '),
            count: broken.length,
          })}
        </span>
      ) : null}

      {run.last_error ? <span className="stamp">{run.last_error}</span> : null}
      {action}
    </div>
  );
}

/* ────────────────────── 4. За период данных не нашлось ────────────────────── */

/**
 * Точек за окно нет. Причин ровно две, и они разные для клиента: либо замер в
 * эти дни не шёл, либо история тарифа короче запрошенного окна. Второе — не
 * пустота, а граница тарифа, и она называется словами.
 */
export function NoDataInPeriod({
  from,
  to,
  historyMonths,
  action,
}: {
  from: string | Date;
  to: string | Date;
  /** Глубина истории тарифа. Если окно старше — дело в тарифе, а не в замере. */
  historyMonths?: number;
  action?: ReactNode;
}) {
  return (
    <Empty
      eyebrow={t('state.no-data.eyebrow')}
      title={t('state.no-data.title')}
      action={action}
    >
      {t('state.no-data.body', { period: period(from, to) })}{' '}
      {historyMonths ? `${t('state.no-data.history', { months: historyMonths })} ` : ''}
      {t('state.no-data.tail')}
    </Empty>
  );
}

/* ────────────────────────── 5. Тариф исчерпан ─────────────────────────────── */

/**
 * Квота кончилась или замеры остановлены.
 *
 * Себестоимости здесь нет и быть не может: наружу уходит только следствие.
 * Владельцу показывается путь к тарифу, остальным — кто это может починить,
 * потому что кнопки оплаты у них нет и рисовать её жестоко.
 */
export function QuotaExhausted({
  quotas,
  canPay,
  action,
}: {
  quotas?: OrganizationQuotas | null;
  /** Есть ли у роли доступ к оплате. У аналитика и наблюдателя его нет. */
  canPay: boolean;
  action?: ReactNode;
}) {
  const blocked = quotas ? !quotas.scan_allowed : false;
  const over = quotas?.over_quota_since;
  // Нулевая квота сама по себе не означает «замер никогда не начинался»: тариф
  // мог закончиться, и тогда причина остановки заполнена, а собранные числа
  // никуда не делись. «Ещё не начинали» — это ноль вопросов И молчание о
  // причине: провизии не было вовсе.
  const neverStarted = Boolean(
    quotas && !quotas.tracked_questions && !quotas.scan_blocked_reason,
  );

  if (neverStarted) {
    return (
      <Empty
        eyebrow={t('state.quota.never.eyebrow')}
        title={t('state.quota.never.title')}
        action={canPay ? action : undefined}
      >
        {t('state.quota.never.lead')}{' '}
        {/* Контракт на стороне онбординга — недельную перепись заводит
            планировщик, а из кабинета запускается только `manual` и `repair`. */}
        {canPay ? t('state.quota.never.can-pay') : t('state.quota.never.cannot-pay')}
      </Empty>
    );
  }

  return (
    <Empty
      eyebrow={
        blocked ? t('state.quota.eyebrow-blocked') : t('state.quota.eyebrow-exhausted')
      }
      title={blocked ? t('state.quota.title-blocked') : t('state.quota.title-exhausted')}
      action={canPay ? action : undefined}
    >
      {quotas && quotas.questions_used !== undefined
        ? `${t('state.quota.tracked', {
            used: quotas.questions_used,
            total: quotas.tracked_questions,
          })} `
        : ''}
      {/* Причина приходит из БД свободным текстом и бывает машинным кодом
          (`payment_failed`). Показываем только то, что человек прочитает. */}
      {blockedReasonProse(quotas?.scan_blocked_reason)}{' '}
      {over ? `${t('state.quota.over-since', { date: dateProse(over) })} ` : ''}
      {t('state.quota.kept')}
      {!canPay ? ` ${t('state.quota.owner-only')}` : ''}
    </Empty>
  );
}

/**
 * Причина остановки замера человеческим текстом.
 *
 * `scan_blocked_reason` — свободная колонка, и в ней вперемешку машинные коды
 * (`payment_failed`, `subscription_expired` — их пишет сам продукт) и фразы,
 * заведённые руками. Код показывать нельзя, фразу — нужно.
 */
const BLOCKED_REASON_CODES = [
  'payment_failed',
  'subscription_past_due',
  'subscription_expired',
  'subscription_cancelled',
  'quota_exhausted',
] as const;

function blockedReasonProse(reason?: string | null): string {
  if (!reason) return t('state.blocked-reason.fallback');
  if ((BLOCKED_REASON_CODES as readonly string[]).includes(reason)) {
    return t(`state.blocked-reason.${reason}`);
  }
  // Похоже на код (латиница без пробелов) — значит показывать его нечего.
  const looksLikeCode = !/\s/.test(reason) && !/[а-яё]/i.test(reason);
  return looksLikeCode ? t('state.blocked-reason.fallback') : reason;
}

/* ──────────────────────── 6. Доступ только на чтение ──────────────────────── */

/**
 * Роль ничего не меняет.
 *
 * Это **не** объяснение к серым кнопкам: кнопок, которых роль не может нажать,
 * на экране нет вовсе (app-kit.md §1). Полоса нужна для другого — чтобы человек
 * понимал, почему он не находит того, что видел коллега, и не искал это в
 * настройках.
 */
export function ReadOnlyNotice({ role }: { role: string }) {
  return (
    <p className="readonly" role="note">
      <span className="tag">{role}</span>
      {t('state.readonly')}
    </p>
  );
}
